import React, { useState, useEffect, useMemo } from 'react';
import {
    StyleSheet,
    Text,
    View,
    TouchableOpacity
} from 'react-native';
import moment from 'moment';
import { useIsFocused } from '@react-navigation/native';
import { AntDesign, Ionicons } from '@expo/vector-icons';
import DataTable from "react-data-table-component";
import { caregiverPushAlarmColumns, useScale, ScaleFn, TablePagination , useDarkMode} from './common';
import Modal from "react-native-modal";
import Data from './Data';
import { getStorage, setStorage } from '../storage';

// ─── 테마 토큰 ────────────────────────────────────────
const DARK = {
    bg:        '#0F172A',
    card:      '#1E293B',
    cardInner: '#0F172A',
    border:    'rgba(99,179,237,0.12)',
    text:      '#E2E8F0',
    textSub:   '#94A3B8',
    muted:     '#a8adb497',
    trackBg:   'rgba(255,255,255,0.05)',
};
const LIGHT = {
    bg:        '#F9FAFB',
    card:      '#FFFFFF',
    cardInner: '#F3F4F6',
    border:    '#E5E7EB',
    text:      '#111827',
    textSub:   '#4B5563',
    muted:     '#9CA3AF',
    trackBg:   'rgba(0,0,0,0.03)',
};

const S = {
    accent:  '#38BDF8',
    ok:      '#34D399',
    err:     '#F87171',
    warn:    '#FB923C',
    replace: '#FBBF24',
    purple:  '#A78BFA',
    pink:    '#F472B6',
};

const OpenModal = ({ modalVisible, setModalVisible, selected, T }: any) => {
    const { scale, s } = useScale();
    const styles = useMemo(() => makeStyles(s), [scale]);
    const date = moment(selected?.MSGDATE).format('YYYY-MM-DD HH:mm:ss');
    return (
        <Modal
            style={{ flex: 1 }}
            backdropColor="black"
            isVisible={modalVisible}
            backdropOpacity={0.7}
            onBackdropPress={() => { setModalVisible(false); }}
        >
            <View style={[styles.modalView, { backgroundColor: T.card, borderColor: T.border }]}>
                <View style={[styles.modalHeader, { borderColor: T.border }]}>
                    <View style={styles.rowstyle}>
                        <Text style={[styles.modalTitle, { color: T.text }]}>전화번호 : {selected?.PHONE}</Text>
                        <TouchableOpacity onPress={() => setModalVisible(false)}>
                            <AntDesign name='close' size={s(28)} color={T.textSub} />
                        </TouchableOpacity>
                    </View>
                    <Text style={[styles.dateFont, { color: T.textSub }]}>{date}</Text>
                </View>
                <View style={[styles.box, { borderColor: T.border, backgroundColor: T.cardInner }]}>
                    <Text style={{ fontSize: s(20), color: T.text }}>{selected?.BODY}</Text>
                </View>
                <View style={styles.buttonArea}>
                    <TouchableOpacity style={[styles.modifyButton, { backgroundColor: S.accent }]}
                        onPress={() => { setModalVisible(!modalVisible); }} >
                        <Text style={styles.buttonFont}>확인</Text>
                    </TouchableOpacity>
                </View>
            </View>
        </Modal >
    )
};

export default function CaregiverPushAlarm({ navigation }: any) {
    const isFocused = useIsFocused();
    const { scale, s } = useScale();
    const styles = useMemo(() => makeStyles(s), [scale]);
    const [modalVisible, setModalVisible] = useState(false);
    const [list, setList] = useState<any[]>([]);
    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = useState(10);
    const _totalPages = Math.max(1, Math.ceil(((list) || []).length / perPage));
    const _curPage = Math.min(Math.max(1, page), _totalPages);
    const pageData = ((list) || []).slice((_curPage - 1) * perPage, _curPage * perPage);
    const [selected, setSelected] = useState<any>();
    const { dark, setDark } = useDarkMode();

    useEffect(() => {
        if (!isFocused) return;
        getStorage('darkMode').then((v: any) => { if (v !== null) setDark(v); });
    }, [isFocused]);

    const T = dark ? DARK : LIGHT;

    const tableStyles = {
        headRow:    { style: { backgroundColor: T.cardInner, borderBottomColor: (dark ? 'rgba(148,163,184,0.30)' : '#CBD5E1'), borderBottomWidth: 1, minHeight: s(40) } },
        headCells:  { style: { color: T.textSub, fontSize: s(12), fontWeight: '600' as any, paddingLeft: s(12), paddingRight: s(12) } },
        rows:       { style: { backgroundColor: T.card, color: T.text, borderBottomColor: (dark ? 'rgba(148,163,184,0.30)' : '#CBD5E1'), borderBottomWidth: 1, minHeight: s(44), '&:hover': { backgroundColor: T.cardInner } } },
        cells:      { style: { color: T.text, fontSize: s(13), paddingLeft: s(12), paddingRight: s(12) } },
        pagination: { style: { backgroundColor: T.card, color: T.textSub, borderTopColor: T.border, borderTopWidth: 1, minHeight: s(44) },
            pageButtonsStyle: { color: T.textSub, fill: T.textSub, '&:disabled': { color: T.muted, fill: T.muted }, '&:hover:not(:disabled)': { backgroundColor: T.cardInner } } },
        noData:     { style: { backgroundColor: T.card, color: T.muted, padding: s(30) } },
    };

    useEffect(() => {
        if (!isFocused) return;
        Data.fetchData('push/getPushMSGLIST', {
        }, (result: any, count: any) => {
          if (count > 0) {
            setList(result);
          } else {
            setList([]);
          }
        }, true);
    }, [isFocused]);

    const onClick = (row: any) => {
        setSelected(row);
        setModalVisible(true);
    };

    return (
        <View style={[styles.container, { backgroundColor: T.bg }]}>
            <View style={[styles.dataTable, { backgroundColor: T.card, borderColor: T.border, shadowColor: dark ? 'transparent' : '#d9dbec' }]}>
                {/* ─── 헤더 ─── */}
                <View style={styles.TopBox}>
                    <View style={{ flexDirection: 'row', alignItems: 'center', gap: s(10) }}>
                        <Text style={{ fontSize: s(18), fontWeight: '700', color: T.text }}>푸시 알림 내역</Text>
                        {list?.length > 0 && (
                            <View style={{ backgroundColor: S.accent + '18', paddingHorizontal: s(8), paddingVertical: s(2), borderRadius: s(10) }}>
                                <Text style={{ fontSize: s(12), fontWeight: '700', color: S.accent }}>{list.length}</Text>
                            </View>
                        )}
                    </View>
                    <View style={{ flexDirection: 'row', alignItems: 'center', gap: s(10) }}>
                        <TouchableOpacity
                            style={[styles.iconBtn, { backgroundColor: dark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.05)' }]}
                            onPress={() => { navigation.navigate('Setting') }}
                        >
                            <Ionicons name="settings-sharp" size={s(16)} color={T.textSub} />
                        </TouchableOpacity>
                    </View>
                </View>
                {/* ─── 테이블 ─── */}
                <DataTable
                    columns={caregiverPushAlarmColumns}
                    data={pageData}
                    customStyles={tableStyles}
                    onRowClicked={(row: any) => { onClick(row) }}
                />
                {(list || []).length > 0 && (
                    <TablePagination
                        rowCount={(list || []).length}
                        perPage={perPage}
                        page={_curPage}
                        onChangePage={setPage}
                        onChangePerPage={(n) => { setPerPage(n); setPage(1); }}
                        T={T}
                        s={s}
                        accent={S.accent}
                    />
                )}
            </View>
            <OpenModal modalVisible={modalVisible} setModalVisible={setModalVisible} selected={selected} T={T} />
        </View>
    );
};

const makeStyles = (s: ScaleFn) => StyleSheet.create({
    container: {
        flex: 1,
        padding: s(24),
    },
    dataTable: {
        borderRadius: s(16),
        borderWidth: 0.5,
        overflow: 'hidden',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.08,
        shadowRadius: s(20),
        elevation: 3,
        paddingTop: s(20),
        paddingHorizontal: s(10),
    },
    TopBox: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        paddingHorizontal: s(6),
        paddingBottom: s(12),
    },
    iconBtn: {
        width: s(34),
        height: s(34),
        borderRadius: s(10),
        justifyContent: 'center',
        alignItems: 'center',
    },
    modalView: {
        margin: s(20),
        borderWidth: 1,
        borderRadius: s(20),
        padding: s(35),
        alignSelf: 'center',
        shadowColor: "#000",
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.25,
        shadowRadius: s(4),
        elevation: 5,
        width: '80%',
    },
    modalHeader: {
        alignItems: 'center',
        paddingBottom: s(10),
        marginBottom: s(10),
        borderBottomWidth: 3,
    },
    rowstyle: {
        width: '100%',
        flexDirection: 'row',
        justifyContent: 'space-between'
    },
    modalTitle: {
        fontSize: s(20),
        fontWeight: '600',
        textAlign: 'left',
        marginRight: s(20),
        lineHeight: s(30)
    },
    box: {
        width: '100%',
        height: s(250),
        justifyContent: 'space-between',
        borderWidth: 1,
        padding: s(10),
        borderRadius: s(10),
    },
    buttonArea: {
        marginTop: s(20),
        flexDirection: 'row',
        alignItems: 'center',
        alignSelf: 'center'
    },
    modifyButton: {
        paddingHorizontal: s(20),
        paddingVertical: s(6),
        justifyContent: 'center',
        alignItems: 'center',
        borderWidth: 0,
        borderRadius: s(10),
        marginRight: s(20)
    },
    buttonFont: {
        fontSize: s(16),
        fontWeight: '600',
        color: '#fff'
    },
    dateFont: {
        fontSize: s(18),
        alignSelf: 'flex-start',
        marginTop: s(5)
    }
});
